import { PaperAirplaneIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import React, { useMemo, useRef } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';

import { FileAttachmentInput } from 'src/shared/components';
import { Question } from 'src/shared/types/question';
import { toastErrorIfFilesOverLimit } from 'src/shared/utils/fileSizeHelpers';

import MarkdownEditor, {
  COMPOSER_EDITOR_CLASS,
  COMPOSER_EDITOR_CONTENT_CLASS
} from 'src/shared/components/MarkdownEditor';
import { addToast, cn } from 'src/shared/components/ui';
import { ButtonV2, Typography } from 'src/shared/components/ui';

import { useApprovalQuestionForm } from './hooks/useApprovalQuestion';
import { useQuestionFiles } from './hooks/useQuestionFiles';
import { useSendQuestion } from './hooks/useSendQuestion';

interface TextQuestionProps {
  question: Question;
  productId: string;
  questionId: number;
  productStage: string;
  [key: string]: any;
}
const TextQuestion: React.FC<TextQuestionProps> = ({
  productId,
  questionId,
  productStage
}) => {
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useApprovalQuestionForm({
    comment: '',
    files: []
  });

  const files = watch('files');
  const comment = watch('comment');

  const questionMutation = useSendQuestion();
  const questionFilesMutation = useQuestionFiles();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isPending =
    questionMutation.isPending || questionFilesMutation.isPending;

  const {
    fields: fileFields,
    append: appendFile,
    remove: removeFile
  } = useFieldArray({
    control,
    name: 'files'
  });

  const onSubmitFiles = () => {
    const filesBody = new FormData();

    files.forEach((file, i) => {
      filesBody.append(`files[${i}]`, file?.file);
      filesBody.append(`captions[${i}]`, file?.caption);
    });

    questionFilesMutation.mutate(
      { body: filesBody, productId, questionId, stageId: productStage },
      {
        onSuccess: () => {
          addToast({
            color: 'success',
            title: 'Question has been successfully answered.'
          });
        }
      }
    );
  };

  const onSubmit = (data: any) => {
    const withFiles = data.files.length > 0;

    if (toastErrorIfFilesOverLimit({ files: data.files, addToast })) {
      return;
    }

    questionMutation.mutate(
      {
        answer: data.comment,
        productId,
        questionId,
        stageId: productStage,
        withFiles
      },
      {
        onSuccess: () => {
          if (withFiles) {
            onSubmitFiles();
          } else {
            addToast({
              color: 'success',
              title: 'Question has been successfully answered.'
            });
          }
        }
      }
    );
  };

  const renderFiles = () => {
    return fileFields.map((field, index) => {
      const file = watch(`files.${index}.file`);
      const caption = watch(`files.${index}.caption`);
      const preview = watch(`files.${index}.preview`);

      return (
        <FileAttachmentInput
          key={field.id}
          fileInputProps={register(`files.${index}.file`)}
          captionInputProps={register(`files.${index}.caption`)}
          fileValue={file}
          captionValue={caption}
          preview={preview}
          onRemove={() => removeFile(index)}
        />
      );
    });
  };

  const isInputDisabled = useMemo(() => {
    return !comment && files.length === 0;
  }, [comment, files]);

  return (
    <div className="relative flex flex-col border rounded-xl border-gray-4 bg-white">
      <form
        className="flex flex-col gap-5 w-full items-start"
        id={`answer-question-form-${questionId}`}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          name="comment"
          control={control}
          render={({ field }) => (
            <MarkdownEditor
              markdown={field.value}
              onChange={field.onChange}
              className={cn('w-full', COMPOSER_EDITOR_CLASS)}
              contentEditableClassName={COMPOSER_EDITOR_CONTENT_CLASS}
            />
          )}
        />
        {errors.comment?.message && (
          <Typography variant="body-2" className="text-danger px-3">
            {errors.comment.message}
          </Typography>
        )}
      </form>

      <div className="flex flex-wrap gap-2 mx-2 pb-2 border-t border-gray-4 pt-2 mt-2">
        {renderFiles()}
      </div>

      <div className="p-2 pt-0 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-grow">
          <input
            type="file"
            ref={fileInputRef}
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const files = e.target.files;

              if (files && files.length > 0) {
                const newEntries = Array.from(files).map((file) => ({
                  file,
                  caption: '',
                  preview: file.type.startsWith('image/')
                    ? URL.createObjectURL(file)
                    : null
                }));

                appendFile(newEntries);
                e.target.value = '';
              }
            }}
          />
          <ButtonV2
            isDisabled={isPending}
            size="md"
            isIconOnly
            color="tertiary"
            onPress={() => fileInputRef.current?.click()}
          >
            <PaperClipIcon className="size-4" />
          </ButtonV2>
        </div>

        <div className="flex items-center justify-end gap-2 flex-1">
          <ButtonV2
            form={`answer-question-form-${questionId}`}
            type="submit"
            size="md"
            isIconOnly
            color="primary"
            isDisabled={isInputDisabled}
            isLoading={isPending}
          >
            <PaperAirplaneIcon className="size-4" />
          </ButtonV2>
        </div>
      </div>
    </div>
  );
};

export default TextQuestion;
